/**
 * HSL Theme Utilities
 *
 * Core functions for the HSL-based theming system.
 * These functions manipulate CSS variables at runtime.
 */

import {
  type AppearancePreset,
  DEFAULT_HUE,
  DEFAULT_APPEARANCE,
  isValidAppearancePreset,
} from './theme-presets';
import { captureClientError } from '@/lib/error-reporting/client';

// ============================================
// STORAGE KEYS
// ============================================

const STORAGE_KEYS = {
  ACCENT_HUE: 'cmg-crm-accent-hue',
  APPEARANCE: 'cmg-crm-appearance',
} as const;

// ============================================
// COLOR CONVERSION UTILITIES
// ============================================

/**
 * Clamp hue to valid 0-360 range
 */
export function clampHue(hue: number): number {
  return ((hue % 360) + 360) % 360;
}

/**
 * Convert HEX color to RGB object
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const clean = hex.replace('#', '').trim();
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;

  const safeN = parseInt(full, 16) || 0; // Default to black if parsing fails
  return {
    r: (safeN >> 16) & 255,
    g: (safeN >> 8) & 255,
    b: safeN & 255,
  };
}

/**
 * Convert RGB to HSL
 */
export function rgbToHsl(
  r: number,
  g: number,
  b: number
): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;

  let h = 0;
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));

  if (d !== 0) {
    switch (max) {
      case rn:
        h = ((gn - bn) / d) % 6;
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      case bn:
        h = (rn - gn) / d + 4;
        break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }

  return {
    h: Math.round(h),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Convert HEX to HSL
 */
export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const { r, g, b } = hexToRgb(hex);
  return rgbToHsl(r, g, b);
}

/**
 * Convert HSL to RGB string (for CSS rgba() fallback)
 * Returns "R, G, B" format for use in CSS: rgba(var(--color-rgb), 0.5)
 */
export function hslToRgbString(h: number, s: number, l: number): string {
  const sNorm = s / 100;
  const lNorm = l / 100;

  const a = sNorm * Math.min(lNorm, 1 - lNorm);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = lNorm - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color);
  };

  return `${f(0)}, ${f(8)}, ${f(4)}`;
}

/**
 * Convert HSL to HEX
 */
export function hslToHex(h: number, s: number, l: number): string {
  const rgb = hslToRgbString(h, s, l);
  const [r, g, b] = rgb.split(', ').map((v) => parseInt(v, 10) || 0);

  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// ============================================
// CONTRAST CALCULATION (WCAG)
// ============================================

/**
 * Calculate relative luminance (WCAG 2.1)
 */
function srgbToLinear(channel: number): number {
  const v = channel / 255;
  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
}

function relativeLuminance(r: number, g: number, b: number): number {
  return (
    0.2126 * srgbToLinear(r) +
    0.7152 * srgbToLinear(g) +
    0.0722 * srgbToLinear(b)
  );
}

/**
 * Calculate contrast ratio between two luminance values
 */
function contrastRatio(l1: number, l2: number): number {
  const [lighter, darker] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Determine if text on a background should be white or black
 * Returns RGB triplet string: "255 255 255" or "0 0 0"
 */
export function getOnBrandColor(brandHue: number, brandSaturation = 92): string {
  // Calculate brand-500 color (L=50%)
  const rgb = hslToRgbString(brandHue, brandSaturation, 50);
  const [r, g, b] = rgb.split(', ').map((v) => parseInt(v, 10) || 0);

  const L = relativeLuminance(r, g, b);
  const whiteContrast = contrastRatio(L, 1); // White luminance = 1
  const blackContrast = contrastRatio(L, 0); // Black luminance = 0

  return whiteContrast >= blackContrast ? '255 255 255' : '0 0 0';
}

// ============================================
// CSS VARIABLE SETTERS
// ============================================

/**
 * Set the accent hue and update all related CSS variables
 */
export function setAccentHue(hue: number): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;
  const h = clampHue(hue);

  // Set brand hue
  root.style.setProperty('--brand-h', String(h));

  // Sync base hue (for tinted grays)
  root.style.setProperty('--base-h', String(h));

  // Calculate complement (H + 180)
  const complementHue = clampHue(h + 180);
  root.style.setProperty('--accent2-h', String(complementHue));

  // Calculate RGB fallback for rgba() usage (brand-500 at L=50%)
  const rgbString = hslToRgbString(h, 92, 50);
  root.style.setProperty('--color-primary-rgb', rgbString);

  // Calculate on-brand text color
  const onBrand = getOnBrandColor(h, 92);
  root.style.setProperty('--on-brand', onBrand);

  // Persist to localStorage
  try {
    localStorage.setItem(STORAGE_KEYS.ACCENT_HUE, String(h));
  } catch (error) {
    captureClientError(error, 'client_runtime', {
      component: 'theme-utils.setAccentHue',
      action: 'localStorage_write',
    }).catch(() => {});
  }
}

/**
 * Set the appearance preset via data attribute
 */
export function setAppearance(preset: AppearancePreset): void {
  if (typeof document === 'undefined') return;

  const root = document.documentElement;

  // Validate preset
  if (!isValidAppearancePreset(preset)) {
    preset = DEFAULT_APPEARANCE;
  }

  // Set data attribute (CSS uses :root[data-appearance="..."])
  root.dataset.appearance = preset;

  // Persist to localStorage
  try {
    localStorage.setItem(STORAGE_KEYS.APPEARANCE, preset);
  } catch (error) {
    captureClientError(error, 'client_runtime', {
      component: 'theme-utils.setAppearance',
      action: 'localStorage_write',
    }).catch(() => {});
  }
}

/**
 * Load and apply theme settings from localStorage
 * Call this on app mount to restore user preferences
 */
export function loadThemeSettings(): {
  hue: number;
  appearance: AppearancePreset;
} {
  if (typeof window === 'undefined') {
    return { hue: DEFAULT_HUE, appearance: DEFAULT_APPEARANCE };
  }

  // Load hue
  let hue = DEFAULT_HUE;
  try {
    const storedHue = localStorage.getItem(STORAGE_KEYS.ACCENT_HUE);
    if (storedHue) {
      const parsed = parseInt(storedHue, 10) || NaN; if (!Number.isNaN(parsed) && parsed >= 0 && parsed <= 360) { hue = parsed; }
    }
  } catch (error) {
    captureClientError(error, 'client_runtime', {
      component: 'theme-utils.loadThemeSettings',
      action: 'localStorage_read_hue',
    }).catch(() => {});
  }

  // Load appearance
  let appearance: AppearancePreset = DEFAULT_APPEARANCE;
  try {
    const storedAppearance = localStorage.getItem(STORAGE_KEYS.APPEARANCE);
    if (storedAppearance && isValidAppearancePreset(storedAppearance)) {
      appearance = storedAppearance;
    }
  } catch (error) {
    captureClientError(error, 'client_runtime', {
      component: 'theme-utils.loadThemeSettings',
      action: 'localStorage_read_appearance',
    }).catch(() => {});
  }

  // Apply settings
  setAccentHue(hue);
  setAppearance(appearance);

  return { hue, appearance };
}

/**
 * Initialize theme from server-provided values
 * Call this in AppLayout with initial values from the database
 */
export function initializeTheme(
  brandHue: number | null,
  appearancePreset: string | null
): void {
  const hue = brandHue ?? DEFAULT_HUE;
  const appearance = isValidAppearancePreset(appearancePreset ?? '')
    ? (appearancePreset as AppearancePreset)
    : DEFAULT_APPEARANCE;

  setAccentHue(hue);
  setAppearance(appearance);
}

// ============================================
// UTILITY EXPORTS
// ============================================

export { STORAGE_KEYS };
